import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Field } from 'formik';
import Grid from '@material-ui/core/Grid';
import { Drawer } from '@material-ui/core';
import CustomMaskField from './MaskField';
import notification from '../../../config/notification';
import FindRequest from '../pages/drawers/FindRequest';
import requestEndpoints from '../../../api/services/requestEndpoints';
import lineEndpoint from '../../../api/services/lineEndpoint';
import { STATUS_CODES } from '../../../config/status';

const RequestProcess = props => {
  const {
    initialState,
    handleBlur,
    handleChange,
    cdLinha,
    canEdit = true,
    servicesFilter,
    requestProcessValues,
    setRequestProcessValues,
    setRequestChanged,
    setFieldValue,
    fieldWidth,
    callBackRequest,
    hasRequest = true,
    hasProcess = true,
    nuProcessRequired = true,
    optionalSeiOrCpa = false
  } = props;

  const requestsfilters = useRef({
    situacoes: [
      { cdSituacao: STATUS_CODES.EM_ANALISE },
      { cdSituacao: STATUS_CODES.AGUARDANDO_AJUSTES_DOCUMENTO },
      { cdSituacao: STATUS_CODES.RETORNO_DIRETOR_GERAL },
    ],
    servicos: servicesFilter,
  }).current;

  const [openDrawer, setOpenDrawer] = useState(false);
  const [loadingProcess, setLoadingProcess] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const canEditProcess = useMemo(() => {
    if (!canEdit) return false;
    return !(
      requestProcessValues.cdProcesso &&
      requestProcessValues.cdProcesso !== '' &&
      requestProcessValues.cdSolicitacao &&
      requestProcessValues.cdSolicitacao !== ''
    );
  }, [canEdit, requestProcessValues]);

  const updateState = values => {
    if (values && values !== '') {
      const temp = {
        ...requestProcessValues,
        ...values,
      };
      setRequestProcessValues(temp);
      Object.keys(values).forEach(key => {
        setFieldValue(key, values[key]);
      });
    }
  };

  const handleSelectRequest = (request, fromClick = false) => {
    updateState({
      cdServico: request.cdServico || '',
      nuSolicitacaoFmt: request.nuSolicitacaoFmt || '',
      nuProcesso: request.nuProcesso || '',
      cdProcesso: request.cdProcesso || '',
      cdSolicitacao: request.cdSolicitacao || '',
      cdSituacao: request.cdSituacao || '',
    });
    if (callBackRequest) callBackRequest(request, fromClick);
  };

  const clearRequestAndProcess = () => {
    updateState({
      cdServico: '',
      nuSolicitacaoFmt: '',
      nuProcesso: '',
      cdProcesso: '',
      cdSolicitacao: '',
      cdSituacao: '',
    });
  };

  const handleProcess = async e => {
    let processNumber = e.target.value;
    await searchProcess(processNumber);
  };

  const searchProcess = async processNumber => {
    setLoadingProcess(true);
    if (processNumber && processNumber !== '') {
      notification("Realizando busca de processo", { autoClose: 2000, type: 'info' });
      try {
        const response = await lineEndpoint.getProcess({ numero: processNumber });
        const process = response?.data;
        if (!process || !process.id || process.id === '') {
          throw Error('O número do processo não existe no CPA');
        }
        updateState({
          processError: false,
          nuProcesso: process.numero,
          cdProcesso: process.id,
        });
        if (hasRequest) await searchRequest(null, process.id);
      } catch (error) {
        clearRequestAndProcess();
        updateState({ processError: true });
        notification(error.message, { toastId: 'VALIDATION_PROCESS', type: 'error' });
      }
    }
    props.setStatus({holding: false})
    setLoadingProcess(false);
  };

  const validateRequest = request => {
    if (servicesFilter && request.cdServico) {
      const found = servicesFilter.find(e => e.cdServico === request.cdServico);
      return !!found;
    }
    return true;
  };

  const searchRequest = async (e, processId) => {
    setLoadingRequest(true);
    if (setRequestChanged) setRequestChanged(true);
    if (processId && processId !== '') {
      const response = await lineEndpoint.getRequestByProcessId({ id: processId });
      if (response && response?.status === 200) {
        const request = response.data;
        if (request !== '' || Object.keys(request).length > 0) {
          const validRequest = validateRequest(request);
          if (!validRequest) {
            setLoadingRequest(false);
            throw new Error(
              'Processo vinculado a uma solicitação que não pertence ao grupo de serviços de linhas'
            );
          }
          updateState({
            cdProcesso: request.cdProcesso,
            cdSolicitacao: request.cdSolicitacao,
            nuSolicitacaoFmt: request.nuSolicitacaoFmt,
            cdServico: request.cdServico,
            cdSituacao: request.cdSituacao,
          });
          if (callBackRequest) callBackRequest(request, true);
        }
      }
    } else {
      let requestNumber = e.target.value;
      if (requestNumber && requestNumber !== '') {
        try {
          const filter = { txtBusca: requestNumber, page: 0, size: 1 };
          if (servicesFilter) {
            filter.servicos = servicesFilter;
          }
          const response = await requestEndpoints.getRequests(filter, '');
          const request = response.data;
          if (!request || !request.content || request.content.length === 0) {
            throw String('Solicitação não encontrada');
          }
          const requestFromServer = request.content[0];
          updateState({
            nuProcesso: requestFromServer.nuProcesso,
            cdProcesso: requestFromServer.cdProcesso,
            cdSolicitacao: requestFromServer.cdSolicitacao,
            cdServico: requestFromServer.cdServico,
            cdSituacao: requestFromServer.cdSituacao,
          });
          if (callBackRequest) callBackRequest(request.content[0], true);
        } catch (error) {
          notification('Solicitação não encontrada', {
            toastId: 'VALIDATION_REQUEST',
            type: 'error',
          });
          clearRequestAndProcess();
        }
      } else {
        clearRequestAndProcess();
      }
    }
    setLoadingRequest(false);
  };

  useEffect(() => {
    (async () => {
      if (initialState.cdSolicitacao) {
        const response = await requestEndpoints.findRequest(
          {
            cdSolicitacao: initialState.cdSolicitacao,
          },
          ''
        );
        if (response && response.status === 200 && response.data !== '') {
          initialState.solicitacao = response.data || {};
          handleSelectRequest(response.data);
        }
      } else {
        handleSelectRequest(initialState);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState]);

  return (
    <>
      {hasRequest && (
        <Grid item xs={12} sm={fieldWidth || 4}>
          <Field
            component={CustomMaskField}
            loading={loadingRequest}
            value={requestProcessValues.nuSolicitacaoFmt || ''}
            name="nuSolicitacaoFmt"
            label="Solicitação"
            mask={'999999/9999'}
            disabled={!canEdit}
            onClickButton={() => setOpenDrawer(true)}
            onBlur={e => {
              handleBlur(e);
              searchRequest(e);
            }}
            onChange={e => {
              handleChange(e);
              updateState({ nuSolicitacaoFmt: e.target.value });
            }}
          />
        </Grid>
      )}
      {hasProcess && (
        <Grid item xs={12} sm={fieldWidth || 4}>
          <Field
            component={CustomMaskField}
            loading={loadingProcess}
            value={requestProcessValues.nuProcesso || ''}
            name="nuProcesso"
            label="Número do Processo"
            mask={'9999.9999/999999'}
            disabled={!canEditProcess}
            validate={() => {
              if (nuProcessRequired) {
                const clearNuprocesso = requestProcessValues.nuProcesso?.trim().replace(/\D/g, '');
                if (!clearNuprocesso) {
                  return 'Campo obrigatório';
                }
              }
              if(optionalSeiOrCpa) {
                if(!props.values.nuProcessoSei) {
                  const clearNuprocesso = requestProcessValues.nuProcesso.trim().replace(/\D/g, '');
                  if (!clearNuprocesso) {
                    return 'Número de processo obrigatório';
                  }
                }
              }
            }}
            error={props.errors['nuProcesso'] && props.touched['nuProcesso']}
            helperText={props.touched['nuProcesso'] && props.errors['nuProcesso']}
            onBlur={e => {
              props.setStatus({holding: true})
              props.setFieldTouched('nuProcesso');
              if(optionalSeiOrCpa) {
                props.setFieldTouched('nuProcessoSei')
                setFieldValue('cdProcesso', '')
              }
              handleBlur(e);
              handleProcess(e);
            }}
            onChange={e => {
              handleChange(e);
              updateState({ nuProcesso: e.target.value, cdProcesso: '' });
            }}
          />
        </Grid>
      )}
      <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <FindRequest
          onSave={values => handleSelectRequest(values, true)}
          onClose={() => setOpenDrawer(false)}
          requestParams={{ boPermissionario: true, cdLinha }}
          filters={requestsfilters}
        />
      </Drawer>
    </>
  );
};

export default RequestProcess;
